/**
 * User API
 * Handles user management operations
 *
 * Endpoints:
 * - GET /api/auth/users/ - List users with filters
 * - POST /api/auth/users/invite/ - Invite new user
 * - PATCH /api/auth/users/{id}/role/ - Update user role
 * - POST /api/auth/users/{id}/campuses/ - Assign campuses
 * - POST /api/auth/users/{id}/activate/ - Activate user
 * - POST /api/auth/users/{id}/deactivate/ - Deactivate user
 * - DELETE /api/auth/users/{id}/ - Remove member from this workspace (owner only)
 */

import apiClient from './client'
import { buildJsonApiQuery } from '@/utils/jsonapi'

/**
 * Normalize role from backend lowercase to frontend uppercase format
 * @param {string} role - Backend role (lowercase)
 * @returns {string} Frontend role (uppercase with underscores)
 */
function normalizeRole(role) {
  const roleMap = {
    'owner': 'OWNER',
    'administrator': 'ADMINISTRATOR',
    'editor': 'EDITOR',
    'viewer': 'VIEWER',
    'global_super_admin': 'GLOBAL_SUPER_ADMIN',
  }
  return roleMap[role?.toLowerCase()] || 'VIEWER'
}

/**
 * Denormalize role from frontend uppercase to backend lowercase format
 * @param {string} role - Frontend role (uppercase)
 * @returns {string} Backend role (lowercase)
 */
function denormalizeRole(role) {
  const roleMap = {
    'OWNER': 'owner',
    'ADMINISTRATOR': 'administrator',
    'EDITOR': 'editor',
    'VIEWER': 'viewer',
    'GLOBAL_SUPER_ADMIN': 'global_super_admin',
  }
  return roleMap[role] || 'viewer'
}

/**
 * Parse user name from name field into first_name/last_name
 * @param {Object} userData - User data object
 * @returns {Object} User object with first_name and last_name
 */
function parseUserName(userData) {
  // Backend returns 'name' field, split it into first_name and last_name
  if (userData.name) {
    const parts = userData.name.trim().split(' ')
    return {
      first_name: parts[0] || '',
      last_name: parts.slice(1).join(' ') || '',
    }
  }
  return {
    first_name: '',
    last_name: '',
  }
}

/**
 * Parse a single user from JSON:API format
 * @param {Object} userData - JSON:API user data object
 * @param {Map} campusMap - Map of campus IDs to campus attributes
 * @returns {Object} Parsed user object
 */
function parseUser(userData, campusMap) {
  const campusIds = userData.relationships?.campuses?.data || []
  return {
    id: userData.id,
    email: userData.attributes.email,
    ...parseUserName(userData.attributes),
    role: normalizeRole(userData.attributes.role),
    campuses: campusIds.map((rel) => ({
      id: rel.id,
      name: campusMap.get(rel.id)?.name || 'Unknown',
    })),
    is_active: userData.attributes.is_active,
    last_login: userData.attributes.last_login,
    date_joined: userData.attributes.date_joined,
  }
}

/**
 * Build campus lookup map from included resources
 * @param {Array} included - JSON:API included resources
 * @returns {Map} Map of campus IDs to campus attributes
 */
function buildCampusMap(included = []) {
  const campusMap = new Map()
  included?.forEach((item) => {
    if (item.type === 'campus') {
      campusMap.set(item.id, item.attributes)
    }
  })
  return campusMap
}

/**
 * Fetch users with optional filters
 * @param {Object} filters - Optional filters
 * @param {string} [filters.role] - Filter by role
 * @param {string} [filters.campus_id] - Filter by campus ID
 * @param {boolean} [filters.is_active] - Filter by active status
 * @param {string} [filters.search] - Search by name or email
 * @param {number} [filters.page] - Page number for pagination
 * @returns {Promise<Object>} Object with users array and pagination meta
 */
export async function getUsers(filters = {}) {
  // Transform frontend filter keys to JSON:API format
  const jsonApiFilters = {}

  if (filters.role) {
    jsonApiFilters.role = denormalizeRole(filters.role)
  }
  if (filters.campus_id) {
    jsonApiFilters.campus = filters.campus_id
  }
  if (filters.is_active !== undefined) {
    jsonApiFilters.is_active = filters.is_active
  }
  if (filters.search) {
    // Use general search that searches across email and name
    jsonApiFilters.search = filters.search
  }

  const params = buildJsonApiQuery({
    filters: jsonApiFilters,
    page: filters.page,
    pageSize: filters.page_size
  })

  const response = await apiClient.get('/auth/users/', { params })

  // Backend returns JSON:API format
  // Format: { data: [{type, id, attributes, relationships}], meta: {...} }
  const userData = response.data.data || []

  // Transform users to frontend format
  const users = userData
    .filter(item => item && item.attributes?.email) // Filter out malformed users
    .map((user) => ({
      id: user.id,
      email: user.attributes.email,
      first_name: user.attributes.name ? user.attributes.name.split(' ')[0] : '',
      last_name: user.attributes.name ? user.attributes.name.split(' ').slice(1).join(' ') : '',
      role: normalizeRole(user.attributes.role),
      // Extract campus relationships - they're in relationships.campuses.data
      campuses: (user.relationships?.campuses?.data || []).map(campus => ({
        id: campus.id,
        type: campus.type
      })),
      is_active: user.attributes.is_active,
      last_login: user.attributes.last_login,
      date_joined: user.attributes.date_joined,
    }))

  return {
    users,
    meta: {
      pagination: response.data.meta?.pagination
    },
  }
}

/**
 * Invite a new user
 * @param {Object} data - User invitation data
 * @param {string} data.email - User email (required)
 * @param {string} data.role - User role (required)
 * @param {Array<string>} [data.campus_ids] - Campus IDs (empty = all campuses)
 * @param {string} [data.first_name] - User first name
 * @param {string} [data.last_name] - User last name
 * @returns {Promise<Object>} Created user attributes
 */
export async function inviteUser(data) {
  const response = await apiClient.post('/auth/users/invite/', {
    ...data,
    role: denormalizeRole(data.role),
  })
  return response.data
}

/**
 * Update a user's role
 * @param {string} userId - User ID to update
 * @param {string} role - New role value
 * @returns {Promise<Object>} Updated user object
 */
export async function updateUserRole(userId, role) {
  const response = await apiClient.patch(`/auth/users/${userId}/role/`, {
    role: denormalizeRole(role),
  })
  const campusMap = buildCampusMap(response.data.included)
  return parseUser(response.data.data, campusMap)
}

/**
 * Assign campuses to a user
 * @param {string} userId - User ID to update
 * @param {Array<string>} campusIds - Array of campus IDs (empty array = all campuses)
 * @returns {Promise<Object>} Updated user object
 */
export async function assignCampuses(userId, campusIds) {
  const response = await apiClient.post(`/auth/users/${userId}/campuses/`, {
    campus_ids: campusIds,
  })
  const campusMap = buildCampusMap(response.data.included)
  return parseUser(response.data.data, campusMap)
}

/**
 * Activate a user account
 * @param {string} userId - User ID to activate
 * @returns {Promise<Object>} Updated user object
 */
export async function activateUser(userId) {
  const response = await apiClient.post(`/auth/users/${userId}/activate/`)
  const campusMap = buildCampusMap(response.data.included)
  return parseUser(response.data.data, campusMap)
}

/**
 * Deactivate a user account
 * @param {string} userId - User ID to deactivate
 * @returns {Promise<Object>} Updated user object
 */
export async function deactivateUser(userId) {
  const response = await apiClient.post(`/auth/users/${userId}/deactivate/`)
  const campusMap = buildCampusMap(response.data.included)
  return parseUser(response.data.data, campusMap)
}

/**
 * Remove a member from the current workspace (owner only).
 *
 * Their reports, dashboards, folders and forms here pass to the caller; the
 * account itself is deleted only when this was their last workspace. The API
 * refuses (400) removing yourself and (409) removing the only owner.
 * @param {string} userId - User ID to remove
 * @returns {Promise<void>}
 */
export async function removeUser(userId) {
  await apiClient.delete(`/auth/users/${userId}/`)
}

/**
 * Fetch pending invitations for the current tenant
 * @returns {Promise<Array>} Array of pending invitations
 */
export async function getPendingInvitations() {
  const response = await apiClient.get('/auth/users/pending_invitations/')
  return response.data.data || []
}

/**
 * Cancel a pending invitation
 * @param {string} invitationId - Invitation ID to cancel
 * @returns {Promise<void>}
 */
export async function cancelInvitation(invitationId) {
  await apiClient.delete(`/auth/users/pending_invitations/${invitationId}/`)
}

/**
 * Resend a pending invitation
 * @param {string} invitationId - Invitation ID to resend
 * @returns {Promise<Object>} Updated invitation data
 */
export async function resendInvitation(invitationId) {
  const response = await apiClient.post(`/auth/users/pending_invitations/${invitationId}/resend/`)
  return response.data.data
}
